"use client";

import Link from "next/link";
import Image from "next/image";
import React, { useMemo } from "react";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";

/* =========================
    TYPES (Strict Data Safety)
========================= */
interface Button {
  id: string;
  url: string;
  text: string;
  type: string; // "primary" | "secondary"
  newTab: boolean;
}

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      alternativeText: string;
    };
  };
}

interface HeroProps {
  data: {
    id: string;
    title: string;
    description: string;
    picture: Picture;
    buttons: Button[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

const Hero_Adaptive = ({ data }: HeroProps) => {
  if (!data?.isLive) return null;

  const picUrl = getStrapiMedia(data.picture?.data?.attributes?.url);
  const picAlt =
    data?.picture?.data?.attributes?.alternativeText ||
    data?.title ||
    "Hero Background";

  // Split title logic for the dual-tone effect
  const { mainTitle, highlightTitle } = useMemo(() => {
    const titleWords = data?.title?.split(" ") || [];
    return {
      mainTitle: titleWords.slice(0, -2).join(" "),
      highlightTitle: titleWords.slice(-2).join(" "),
    };
  }, [data?.title]);

  const TitleTag = data?.useH1Tag ? "h1" : "h2";

  return (
    <section className="relative w-full h-[550px] md:h-[700px] lg:h-[85vh] overflow-hidden bg-neutral-100 flex items-center">
      
      {/* 1. Adaptive Background Image - Anchored to the right on desktop */}
      <div className="absolute inset-0 lg:left-1/4 transition-transform duration-[10000ms] ease-out">
        {picUrl && (
          <Image
            src={picUrl}
            alt={picAlt}
            fill
            className="object-cover object-center lg:object-right"
            priority
            quality={95}
          />
        )}
        {/* Mobile Overlay: Darkens the image for text readability on small screens */}
        <div className="absolute inset-0 bg-black/40 lg:bg-transparent z-10" />
      </div>

      {/* 2. Content Layer: Glassmorphism Card that adapts to screen size */}
      <div className="relative z-20 w-full max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pointer-events-none">
        <div className="max-w-xl lg:max-w-2xl bg-white/10 lg:bg-white p-8 md:p-12 lg:p-16 backdrop-blur-xl lg:backdrop-blur-none lg:shadow-2xl border border-white/20 lg:border-none rounded-[2rem] lg:rounded-none pointer-events-auto">
          
          {/* Architectural Typography */}
          <div className="mb-6">
            <TitleTag className="text-4xl md:text-6xl lg:text-7xl font-black text-white lg:text-neutral-900 uppercase italic tracking-tighter leading-[0.9]">
              {mainTitle} <br />
              <span style={{ color: "var(--secondary)" }}>
                {highlightTitle}
              </span>
            </TitleTag>
          </div>

          {/* Adaptive Description */}
          <p className="text-base md:text-lg text-white/90 lg:text-neutral-600 mb-10 font-medium leading-relaxed">
            {data?.description}
          </p>

          {/* 3. Button Container */}
          <div className="flex flex-wrap gap-4 md:gap-6">
            {data?.buttons?.map((btn, index) => {
              const isPrimary = btn.type === "primary" || (!btn.type && index === 0);
              
              return (
                <Link
                  key={btn.id || index}
                  href={btn?.url || "#"}
                  target={btn?.newTab ? "_blank" : "_self"}
                  rel={btn?.newTab ? "noopener noreferrer" : undefined}
                  className={`
                    px-8 md:px-10 py-4 font-black uppercase text-[10px] md:text-[11px] tracking-widest transition-all duration-300 active:scale-95 text-center
                    ${
                      isPrimary
                        ? "text-white shadow-xl hover:opacity-90"
                        : "bg-neutral-900 text-white lg:bg-neutral-100 lg:text-neutral-900 hover:bg-neutral-200"
                    }
                  `}
                  style={{
                    backgroundColor: isPrimary ? "var(--secondary)" : undefined,
                  }}
                >
                  {btn?.text}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero_Adaptive;